import {
  resolveCanonicalHost,
} from "@cue-the-night/host-engine";
import type {
  HostedGameSnapshot,
  HostedPartySnapshot,
} from "@cue-the-night/transport";

import {
  startOwnerApp,
} from "./owner";

import {
  resolveWebSocketUrl,
} from "./config";

import {
  HostedPartyClient,
  HostedPartyClientError,
} from "./hosted-party-client";

import {
  isValidJoinCode,
  normalizeJoinCode,
  readJoinCode,
} from "./join-route";

import {
  renderPlayerGame,
} from "./game-ui";

import {
  playerAvatarAsset,
  playerAvatarChoices,
  playerRingAsset,
} from "./premium-gameplay-assets";

import {
  installVisualFoundation,
} from "./visual-foundation";

installVisualFoundation();

const app =
  document.querySelector(
    "#app",
  );

if (!(app instanceof HTMLElement)) {
  throw new Error(
    "Player Web root is missing.",
  );
}

const isOwnerRoute =
  window.location.pathname ===
    "/owner" ||
  window.location.pathname
    .startsWith("/owner/");

if (isOwnerRoute) {
  void startOwnerApp(app);
} else {

const initialCode =
  readJoinCode(
    window.location.pathname,
    window.location.search,
  ) ?? "";

const initialAvatarId =
  playerAvatarChoices[0].id;

const playerAvatarOptionsHtml =
  playerAvatarChoices
    .map(
      (
        avatar,
        index,
      ) => `
        <button
          class="join-avatar-choice${index === 0 ? " is-selected" : ""}"
          type="button"
          data-avatar-id="${avatar.id}"
          aria-label="${escapeHtml(avatar.label)}"
          aria-pressed="${index === 0 ? "true" : "false"}"
        >
          <span class="join-avatar-visual">
            <img
              class="join-avatar-image"
              src="${playerAvatarAsset(avatar.id)}"
              alt=""
            />

            <img
              class="join-avatar-ring"
              src="${playerRingAsset}"
              alt=""
            />
          </span>

          <span class="join-avatar-label">
            ${escapeHtml(avatar.label)}
          </span>
        </button>
      `,
    )
    .join("");

app.innerHTML = `
  <section class="shell core-screen">
    <header class="core-header">
      <p class="eyebrow">
        CUE THE NIGHT
      </p>

      <h1>
        Join<br />the night.
      </h1>

      <p class="subtitle">
        Enter the code shown by your host.
      </p>
    </header>

    <section class="card core-surface" id="panel">
      <form id="join-form">
        <label>
          Join code

          <input
            id="join-code"
            class="code-input"
            autocomplete="one-time-code"
            maxlength="6"
            value="${initialCode}"
            required
          />
        </label>

        <label>
          Your name

          <input
            id="display-name"
            autocomplete="nickname"
            maxlength="32"
            required
          />
        </label>

        <fieldset class="join-avatar-fieldset">
          <legend>
            Choose your avatar
          </legend>

          <p class="join-avatar-help">
            Pick your look for tonight.
          </p>

          <div class="join-avatar-selected">
            <span
              class="join-avatar-visual join-avatar-selected-visual"
            >
              <img
                class="join-avatar-image"
                id="selected-avatar-image"
                src="${playerAvatarAsset(initialAvatarId)}"
                alt=""
              />

              <img
                class="join-avatar-ring"
                src="${playerRingAsset}"
                alt=""
              />
            </span>

            <span class="join-avatar-selected-copy">
              <span class="meta">
                YOUR AVATAR
              </span>

              <strong id="selected-avatar-label">
                ${escapeHtml(playerAvatarChoices[0].label)}
              </strong>

              <button
                class="join-avatar-toggle"
                id="avatar-picker-toggle"
                type="button"
                aria-expanded="false"
                aria-controls="avatar-picker"
              >
                Change avatar
              </button>
            </span>
          </div>

          <div
            class="join-avatar-grid"
            id="avatar-picker"
            hidden
          >
            ${playerAvatarOptionsHtml}
          </div>
        </fieldset>

        <button
          id="join-button"
          type="submit"
        >
          Join Party
        </button>

        <p
          class="status"
          id="status"
        ></p>
      </form>
    </section>
  </section>
`;

const form =
  document.querySelector(
    "#join-form",
  );

const codeInput =
  document.querySelector(
    "#join-code",
  );

const nameInput =
  document.querySelector(
    "#display-name",
  );

const button =
  document.querySelector(
    "#join-button",
  );

const status =
  document.querySelector(
    "#status",
  );

const panel =
  document.querySelector(
    "#panel",
  );

if (
  !(form instanceof HTMLFormElement) ||
  !(codeInput instanceof HTMLInputElement) ||
  !(nameInput instanceof HTMLInputElement) ||
  !(button instanceof HTMLButtonElement) ||
  !(status instanceof HTMLElement) ||
  !(panel instanceof HTMLElement)
) {
  throw new Error(
    "Player Web controls are missing.",
  );
}

const panelElement:
  HTMLElement = panel;

const buttonElement:
  HTMLButtonElement = button;

const statusElement:
  HTMLElement = status;

const avatarButtons =
  Array.from(
    document
      .querySelectorAll<HTMLButtonElement>(
        "[data-avatar-id]",
      ),
  );

const avatarPicker =
  document.querySelector(
    "#avatar-picker",
  );

const avatarPickerToggle =
  document.querySelector(
    "#avatar-picker-toggle",
  );

const selectedAvatarImage =
  document.querySelector(
    "#selected-avatar-image",
  );

const selectedAvatarLabel =
  document.querySelector(
    "#selected-avatar-label",
  );

if (
  avatarButtons.length !==
    playerAvatarChoices.length ||
  !(avatarPicker instanceof HTMLElement) ||
  !(avatarPickerToggle instanceof HTMLButtonElement) ||
  !(selectedAvatarImage instanceof HTMLImageElement) ||
  !(selectedAvatarLabel instanceof HTMLElement)
) {
  throw new Error(
    "Player avatar choices are missing.",
  );
}

let selectedAvatarId:
  string = initialAvatarId;

avatarPickerToggle.addEventListener(
  "click",
  () => {
    const willOpen =
      avatarPicker.hidden;

    avatarPicker.hidden =
      !willOpen;

    avatarPickerToggle.setAttribute(
      "aria-expanded",
      willOpen
        ? "true"
        : "false",
    );

    avatarPickerToggle.textContent =
      willOpen
        ? "Close avatars"
        : "Change avatar";
  },
);

for (
  const avatarButton of
  avatarButtons
) {
  avatarButton.addEventListener(
    "click",
    () => {
      const avatarId =
        avatarButton.dataset
          .avatarId;

      if (!avatarId) {
        return;
      }

      selectedAvatarId =
        avatarId;

      const selectedAvatar =
        playerAvatarChoices.find(
          (candidate) =>
            candidate.id ===
              avatarId,
        );

      if (!selectedAvatar) {
        return;
      }

      selectedAvatarImage.src =
        playerAvatarAsset(
          selectedAvatar.id,
        );

      selectedAvatarLabel.textContent =
        selectedAvatar.label;

      avatarPicker.hidden =
        true;

      avatarPickerToggle.setAttribute(
        "aria-expanded",
        "false",
      );

      avatarPickerToggle.textContent =
        "Change avatar";

      for (
        const choice of
        avatarButtons
      ) {
        const selected =
          choice ===
          avatarButton;

        choice.classList.toggle(
          "is-selected",
          selected,
        );

        choice.setAttribute(
          "aria-pressed",
          selected
            ? "true"
            : "false",
        );
      }
    },
  );
}

let client:
  HostedPartyClient | undefined;

let unsubscribeSnapshots:
  (() => void) | undefined;

let unsubscribeGameSnapshots:
  (() => void) | undefined;

let currentPartySnapshot:
  HostedPartySnapshot | undefined;

let currentGameSnapshot:
  HostedGameSnapshot | undefined;

let currentParticipantId:
  string | undefined;

let currentDisplayName =
  "";

codeInput.addEventListener(
  "input",
  () => {
    codeInput.value =
      normalizeJoinCode(
        codeInput.value,
      );
  },
);

function reconnectStorageKey(
  code: string,
): string {
  return `ctn.reconnect.${code}`;
}

function canonicalizeJoinUrl(
  code: string,
): void {
  const url =
    new URL(
      window.location.href,
    );

  url.pathname =
    `/join/${encodeURIComponent(code)}`;

  url.searchParams.delete(
    "code",
  );

  window.history.replaceState(
    null,
    "",
    url,
  );
}

function escapeHtml(
  value: string,
): string {
  const element =
    document.createElement(
      "div",
    );

  element.textContent =
    value;

  return element.innerHTML;
}

function resolveOwnName(
  snapshot:
    HostedPartySnapshot,
  participantId:
    string,
  fallback:
    string,
): string {
  return (
    snapshot.participants.find(
      (participant) =>
        participant.participantId ===
        participantId,
    )?.displayName ??
    fallback
  );
}

function showLobby(
  snapshot:
    HostedPartySnapshot,
  participantId:
    string,
  fallbackDisplayName:
    string,
): void {
  currentParticipantId =
    participantId;

  currentPartySnapshot =
    snapshot;

  currentDisplayName =
    resolveOwnName(
      snapshot,
      participantId,
      fallbackDisplayName,
    );

  if (
    currentGameSnapshot &&
    client
  ) {
    renderPlayerGame({
      panel:
        panelElement,
      party:
        snapshot,
      game:
        currentGameSnapshot,
      participantId,
      client,
      onGameSnapshot:
        (nextGameSnapshot) => {
          currentGameSnapshot =
            nextGameSnapshot;

          if (
            currentPartySnapshot &&
            currentParticipantId &&
            client
          ) {
            renderPlayerGame({
              panel:
                panelElement,
              party:
                currentPartySnapshot,
              game:
                nextGameSnapshot,
              participantId:
                currentParticipantId,
              client,
              onGameSnapshot:
                (latest) => {
                  currentGameSnapshot =
                    latest;

                  showLobby(
                    currentPartySnapshot!,
                    currentParticipantId!,
                    currentDisplayName,
                  );
                },
            });
          }
        },
    });

    return;
  }

  const activeCount =
    snapshot.participants.filter(
      (participant) =>
        participant.status ===
        "ACTIVE",
    ).length;

  const partyStarted =
    snapshot.status ===
      "ACTIVE";

  const roster =
    snapshot.participants
      .map((participant) => {
        const isYou =
          participant.participantId ===
          participantId;

        return `
          <li class="roster-player">
            <div class="roster-player-identity">
              <span class="roster-avatar-shell">
                <img
                  class="roster-avatar"
                  src="${playerAvatarAsset(participant.avatarId)}"
                  alt=""
                />

                <img
                  class="roster-avatar-ring"
                  src="${playerRingAsset}"
                  alt=""
                />
              </span>

              <strong>
                ${escapeHtml(participant.displayName)}
                ${isYou ? '<span class="you-tag">YOU</span>' : ""}
              </strong>
            </div>

            <span
              class="presence ${participant.status === "ACTIVE" ? "active" : "disconnected"}"
            >
              ${participant.status === "ACTIVE" ? "Connected" : "Reconnecting"}
            </span>
          </li>
        `;
      })
      .join("");

  panelElement.innerHTML = `
    <div class="success">
      <div>
        <p class="eyebrow">
          YOU'RE IN
        </p>

        <div class="welcome">
          Welcome, ${escapeHtml(currentDisplayName)}.
        </div>
      </div>

      <div>
        <div class="meta">
          Party code
        </div>

        <div class="join-code">
          ${escapeHtml(snapshot.joinCode)}
        </div>
      </div>

      <div class="meta">
        Host:
        ${escapeHtml(resolveCanonicalHost(snapshot.activeVirtualHostId)?.displayName ?? snapshot.activeVirtualHostId)}
        ·
        ${activeCount}
        connected
      </div>

      <div class="roster-block">
        <div class="roster-heading">
          In the lobby
        </div>

        <ul class="roster">
          ${roster}
        </ul>
      </div>

      ${
        partyStarted
          ? `
            <div class="starting">
              <p class="eyebrow">
                THE NIGHT IS STARTING
              </p>

              <strong>
                ${escapeHtml(resolveCanonicalHost(snapshot.activeVirtualHostId)?.displayName ?? snapshot.activeVirtualHostId)}
                is ready.
              </strong>

              <div class="meta">
                Hang tight…
              </div>
            </div>
          `
          : `
            <div class="waiting">
              Waiting for the host to start the night…
            </div>
          `
      }
    </div>
  `;
}

let playerReconnectInFlight =
  false;

let playerClosing =
  false;

function renderPlayerReconnect(
  message:
    string,
): void {
  currentGameSnapshot =
    undefined;

  panelElement.innerHTML = `
    <div class="success">
      <p class="eyebrow">
        CONNECTION INTERRUPTED
      </p>

      <div class="welcome">
        Reconnecting…
      </div>

      <div class="waiting">
        ${escapeHtml(message)}
      </div>
    </div>
  `;
}

function waitForPlayerReconnect(
  delayMs:
    number,
): Promise<void> {
  return new Promise<void>(
    (resolve) => {
      window.setTimeout(
        () => resolve(),
        delayMs,
      );
    },
  );
}

async function recoverPlayerConnection():
Promise<void> {
  if (
    playerClosing ||
    playerReconnectInFlight ||
    !currentParticipantId
  ) {
    return;
  }

  const code =
    currentPartySnapshot
      ?.joinCode ??
    initialCode;

  if (
    !code ||
    !isValidJoinCode(
      code,
    )
  ) {
    return;
  }

  const savedToken =
    localStorage.getItem(
      reconnectStorageKey(
        code,
      ),
    );

  if (!savedToken) {
    renderPlayerReconnect(
      "Your reconnect token is missing. Refresh and join again.",
    );

    return;
  }

  playerReconnectInFlight =
    true;

  renderPlayerReconnect(
    "Restoring the latest live game state.",
  );

  try {
    for (
      let attempt = 1;
      attempt <= 6;
      attempt++
    ) {
      try {
        const resumed =
          await resumeSavedSession(
            code,
            savedToken,
            currentDisplayName,
          );

        if (resumed) {
          return;
        }

        client =
          undefined;

        renderPlayerReconnect(
          "Your previous session expired. Refresh and join again.",
        );

        return;
      } catch {
        client?.disconnect();

        client =
          undefined;

        if (
          attempt ===
            6
        ) {
          break;
        }

        renderPlayerReconnect(
          `Connection still unavailable. Retrying ${attempt + 1}/6…`,
        );

        await waitForPlayerReconnect(
          Math.min(
            5_000,
            attempt * 1_000,
          ),
        );
      }
    }

    renderPlayerReconnect(
      "The server is still unavailable. Refresh this screen to retry.",
    );
  } finally {
    playerReconnectInFlight =
      false;
  }
}

async function createConnectedClient():
Promise<HostedPartyClient> {
  client?.disconnect();

  unsubscribeSnapshots?.();
  unsubscribeGameSnapshots?.();

  const nextClient =
    new HostedPartyClient(
      resolveWebSocketUrl(),
      () => {
        void recoverPlayerConnection();
      },
    );

  unsubscribeSnapshots =
    nextClient
      .subscribeToPartySnapshots(
        (message) => {
          if (
            !currentParticipantId
          ) {
            return;
          }

          if (
            !message.snapshot
              .currentGameSessionId
          ) {
            currentGameSnapshot =
              undefined;
          }

          showLobby(
            message.snapshot,
            currentParticipantId,
            currentDisplayName,
          );
        },
      );

  unsubscribeGameSnapshots =
    nextClient
      .subscribeToGameSnapshots(
        (message) => {
          currentGameSnapshot =
            message.snapshot;

          if (
            currentPartySnapshot &&
            currentParticipantId
          ) {
            showLobby(
              currentPartySnapshot,
              currentParticipantId,
              currentDisplayName,
            );
          }
        },
      );

  client =
    nextClient;

  await nextClient.connect();

  return nextClient;
}

async function resumeSavedSession(
  code: string,
  savedToken: string,
  fallbackDisplayName:
    string,
): Promise<boolean> {
  const nextClient =
    await createConnectedClient();

  try {
    const resumed =
      await nextClient.resumeParty(
        savedToken,
      );

    if (
      resumed.role !==
        "PLAYER" ||
      !resumed.participantId
    ) {
      throw new Error(
        "Saved session is not a player session.",
      );
    }

    canonicalizeJoinUrl(
      code,
    );

    showLobby(
      resumed.snapshot,
      resumed.participantId,
      fallbackDisplayName,
    );

    return true;
  } catch (error) {
    if (
      error instanceof
        HostedPartyClientError &&
      error.code ===
        "INVALID_RECONNECT_TOKEN"
    ) {
      localStorage.removeItem(
        reconnectStorageKey(
          code,
        ),
      );

      nextClient.disconnect();

      return false;
    }

    throw error;
  }
}

form.addEventListener(
  "submit",
  async (event) => {
    event.preventDefault();

    const code =
      normalizeJoinCode(
        codeInput.value,
      );

    const displayName =
      nameInput.value.trim();

    if (
      !isValidJoinCode(code)
    ) {
      statusElement.textContent =
        "Enter a valid 6-character party code.";

      return;
    }

    if (
      displayName.length === 0
    ) {
      statusElement.textContent =
        "Enter your name.";

      return;
    }

    buttonElement.disabled =
      true;

    statusElement.textContent =
      "Connecting to the night…";

    try {
      const storageKey =
        reconnectStorageKey(
          code,
        );

      const savedToken =
        localStorage.getItem(
          storageKey,
        );

      if (savedToken) {
        statusElement.textContent =
          "Reconnecting…";

        const resumed =
          await resumeSavedSession(
            code,
            savedToken,
            displayName,
          );

        if (resumed) {
          return;
        }
      }

      statusElement.textContent =
        "Joining party…";

      const nextClient =
        await createConnectedClient();

      const joined =
        await nextClient.joinParty(
          code,
          displayName,
          selectedAvatarId,
        );

      localStorage.setItem(
        storageKey,
        joined.reconnectToken,
      );

      canonicalizeJoinUrl(
        code,
      );

      showLobby(
        joined.snapshot,
        joined.participantId,
        displayName,
      );
    } catch (error) {
      buttonElement.disabled =
        false;

      if (
        error instanceof
          HostedPartyClientError
      ) {
        statusElement.textContent =
          error.message;

        return;
      }

      statusElement.textContent =
        error instanceof Error
          ? error.message
          : "Unable to join the party.";
    }
  },
);

async function tryAutomaticResume():
Promise<void> {
  if (
    !initialCode ||
    !isValidJoinCode(
      initialCode,
    )
  ) {
    return;
  }

  const savedToken =
    localStorage.getItem(
      reconnectStorageKey(
        initialCode,
      ),
    );

  if (!savedToken) {
    return;
  }

  buttonElement.disabled =
    true;

  statusElement.textContent =
    "Reconnecting to the night…";

  try {
    const resumed =
      await resumeSavedSession(
        initialCode,
        savedToken,
        "",
      );

    if (!resumed) {
      buttonElement.disabled =
        false;

      statusElement.textContent =
        "Your previous session expired. Join again.";
    }
  } catch (error) {
    buttonElement.disabled =
      false;

    statusElement.textContent =
      error instanceof Error
        ? error.message
        : "Unable to reconnect.";
  }
}

void tryAutomaticResume();

window.addEventListener(
  "beforeunload",
  () => {
    playerClosing =
      true;

    unsubscribeSnapshots?.();
    unsubscribeGameSnapshots?.();
    client?.disconnect();
  },
);
}
